import { Component, OnInit } from '@angular/core';
import { NtripConnection } from '../../models/ntrip-connection';
import { UserService } from '../../services/user.service';
import { PermissionService } from '../../services/permission.service';

type NtripLogType = 'access' | 'ntripcaster' | 'usage';

interface ParsedLogView {
  mode: 'table' | 'text' | 'empty';
  headers: string[];
  rows: string[][];
  raw: string;
}

interface NtripLogPanel {
  type: NtripLogType;
  title: string;
  content: string;
  meta: string;
  loadingLatest: boolean;
  downloading: boolean;
  parsed: ParsedLogView;
}

@Component({
  selector: 'app-ntrip-server-admin',
  templateUrl: './ntrip-server-admin.component.html',
  styleUrls: ['./ntrip-server-admin.component.scss']
})
export class NtripServerAdminComponent implements OnInit {
  allNtripConnections: NtripConnection[] = [];
  loadingAllConnections = false;
  isAdmin = false;

  private readonly emptyParsed: ParsedLogView = { mode: 'empty', headers: [], rows: [], raw: '' };

  logPanels: NtripLogPanel[] = [
    { type: 'access', title: 'User Access logs', content: '', meta: '', loadingLatest: false, downloading: false, parsed: { ...this.emptyParsed } },
    { type: 'ntripcaster', title: 'NtripCaster Server logs', content: '', meta: '', loadingLatest: false, downloading: false, parsed: { ...this.emptyParsed } },
    { type: 'usage', title: 'NtripCaster usage logs', content: '', meta: '', loadingLatest: false, downloading: false, parsed: { ...this.emptyParsed } }
  ];

  constructor(private userService: UserService, private perms: PermissionService) {}

  ngOnInit(): void {
    this.checkAdminPermissions();
  }

  checkAdminPermissions(): void {
    this.perms.ensureFresh();
    this.isAdmin = this.perms.isAdminOrEnt();
    if (this.isAdmin) {
      this.getAllNtripConnections();
    }
  }

  getAllNtripConnections(): void {
    this.loadingAllConnections = true;
    this.userService.getAllNtripConnections().subscribe({
      next: (data) => { this.allNtripConnections = data || []; this.loadingAllConnections = false; },
      error: _ => { this.loadingAllConnections = false; }
    });
  }

  showLatestLog(panel: NtripLogPanel): void {
    if (panel.loadingLatest) return;
    panel.loadingLatest = true;
    panel.meta = '';
    panel.parsed = { ...this.emptyParsed };
    this.userService.getNtripLogLatest(panel.type).subscribe({
      next: (res) => {
        panel.loadingLatest = false;
        panel.content = res?.content ?? '';
        panel.parsed = this.parseLogContent(panel.type, panel.content);
        const sizeKb = res?.fileSizeBytes != null ? (res.fileSizeBytes / 1024).toFixed(1) : '?';
        const trunc = res?.truncated ? ` (showing last ${res.returnedLines} of more lines)` : ` (${res.returnedLines} lines)`;
        panel.meta = `${res.fileName || ''} · ${sizeKb} KB${trunc}`;
      },
      error: (err) => {
        panel.loadingLatest = false;
        const msg = typeof err?.error === 'string'
          ? err.error
          : (err?.error?.message || err?.error?.error || err?.message || 'Failed to load log');
        panel.content = '';
        panel.parsed = { ...this.emptyParsed };
        panel.meta = msg;
      }
    });
  }

  downloadAllLogs(panel: NtripLogPanel): void {
    if (panel.downloading) return;
    panel.downloading = true;
    this.userService.downloadNtripLogsZip(panel.type).subscribe({
      next: (blob) => {
        panel.downloading = false;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${panel.type}-logs.zip`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      },
      error: async (err) => {
        panel.downloading = false;
        let msg = 'Download failed';
        try {
          if (err?.error instanceof Blob) {
            const text = await err.error.text();
            try {
              const json = JSON.parse(text);
              msg = json.message || json.error || text || msg;
            } catch {
              msg = text || msg;
            }
          } else {
            msg = err?.error?.message || err?.message || msg;
          }
        } catch { /* keep default */ }
        panel.meta = msg;
      }
    });
  }

  /** Parse raw log text into a table view based on log type. */
  parseLogContent(type: NtripLogType, content: string): ParsedLogView {
    const raw = content ?? '';
    const lines = raw.split(/\r?\n/).filter(l => l.trim().length > 0);
    if (lines.length === 0) {
      return { mode: 'empty', headers: [], rows: [], raw };
    }

    if (type === 'access') {
      return this.parseAccessCsv(lines, raw);
    }
    if (type === 'usage') {
      return this.parseUsageLines(lines, raw);
    }
    return this.parseBracketLines(lines, raw);
  }

  private parseAccessCsv(lines: string[], raw: string): ParsedLogView {
    const headers = ['Date', 'Time', 'User', 'IP', 'Station', 'Client', 'Seconds', 'Bytes'];
    const rows: string[][] = [];
    let csvLike = 0;

    for (const line of lines) {
      // Skip optional header row if present
      if (/^date\s*,\s*time\s*,/i.test(line)) continue;
      const cols = this.splitCsvLine(line);
      if (cols.length >= 6) {
        csvLike++;
        const padded = [...cols];
        while (padded.length < 8) padded.push('');
        rows.push(padded.slice(0, 8));
      } else {
        // Keep odd lines visible in last column
        rows.push(['', '', '', '', '', line, '', '']);
      }
    }

    // If mostly not CSV, fall back to plain text
    if (csvLike < Math.max(1, Math.floor(lines.length * 0.5))) {
      return { mode: 'text', headers: [], rows: [], raw };
    }
    return { mode: 'table', headers, rows, raw };
  }

  private parseBracketLines(lines: string[], raw: string): ParsedLogView {
    const headers = ['Timestamp', 'Thread', 'Message'];
    const rows: string[][] = [];
    const re = /^\[([^\]]+)\]\s*\[([^\]]*)\]\s*(.*)$/;
    let matched = 0;

    for (const line of lines) {
      const m = line.match(re);
      if (m) {
        matched++;
        rows.push([m[1], m[2], m[3] ?? '']);
      } else {
        rows.push(['', '', line]);
      }
    }

    if (matched < Math.max(1, Math.floor(lines.length * 0.5))) {
      return { mode: 'text', headers: [], rows: [], raw };
    }
    return { mode: 'table', headers, rows, raw };
  }

  /**
   * Usage lines share [ts][thread] prefix; bandwidth lines also expose metric columns.
   */
  private parseUsageLines(lines: string[], raw: string): ParsedLogView {
    const headers = ['Timestamp', 'Thread', 'Bandwidth', 'Sources', 'Clients', 'Admins', 'Message'];
    const rows: string[][] = [];
    const re = /^\[([^\]]+)\]\s*\[([^\]]*)\]\s*(.*)$/;
    const bwRe = /Bandwidth:([^\s]+)\s+Sources:(\d+)\s+Clients:(\d+)\s+Admins:(\d+)/i;
    let matched = 0;

    for (const line of lines) {
      const m = line.match(re);
      if (!m) {
        rows.push(['', '', '', '', '', '', line]);
        continue;
      }
      matched++;
      const rest = m[3] ?? '';
      const bw = rest.match(bwRe);
      if (bw) {
        rows.push([m[1], m[2], bw[1], bw[2], bw[3], bw[4], '']);
      } else {
        rows.push([m[1], m[2], '', '', '', '', rest]);
      }
    }

    if (matched < Math.max(1, Math.floor(lines.length * 0.5))) {
      return { mode: 'text', headers: [], rows: [], raw };
    }
    return { mode: 'table', headers, rows, raw };
  }

  /** Simple CSV split that keeps commas inside fields only when quoted (rare here). */
  private splitCsvLine(line: string): string[] {
    const out: string[] = [];
    let cur = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        inQuotes = !inQuotes;
        continue;
      }
      if (ch === ',' && !inQuotes) {
        out.push(cur.trim());
        cur = '';
        continue;
      }
      cur += ch;
    }
    out.push(cur.trim());
    return out;
  }
}
